/**
 * 保存日数を変える・「事故対応」の指定を外すときの、確認の窓と結果の言葉（issue #410・決定22）。
 *
 * 変えたあとの日数を過ぎたメッセージと写真などのファイルは、押した**その場で**消え始めます（夜まで待ちません）。
 * そのため確認の窓で「いま◯件のメッセージと◯件の写真などのファイルが消えます。元に戻せません。」と件数をお見せし、
 * 押したあとは実際に消した件数をお返しします。トークの画面と1対1の「事故対応」の画面で同じ言葉を使います。
 */
import { ApiError } from '../api/client.js';
import type { RetentionChangePreview, RetentionPurgeOutcome } from '../api/types.js';
import { ROLE_REQUIREMENTS, type RoleWording } from '../auth/role-wording.js';

/**
 * 確認の窓でお見せした件数より多く消えることになっていたので、サーバが何も変えずに断った（issue #410）。
 * 窓を開いてから押すまでの間に、ほかの方が変えた・画面が古かった、などのとき。
 */
export function countChangedSinceConfirm(caught: unknown): boolean {
  return caught instanceof ApiError && caught.code === 'retention_count_changed';
}

/** 件数が変わって断られたときの次の一手（画面は読み直す）。 */
export const COUNT_CHANGED_NEXT_STEP =
  '画面を最新の内容に読み直しました。もう一度操作すると、いま消える件数を確かめる窓が出ます。';

function n(value: number): string {
  return value.toLocaleString('ja-JP');
}

/** いま消えるものがあるか（窓の「元に戻せません」を赤で出すかどうか）。 */
export function losesMessages(preview: RetentionChangePreview | null): boolean {
  return preview !== null && preview.message_count > 0;
}

/** 確認の窓の本文に添える「いま◯件…が消えます。」（消えるものが無ければ、その旨）。 */
export function expiringSentence(preview: RetentionChangePreview): string {
  if (preview.retention_days === null) {
    return '保存期間は無期限なので、自動では消えません。';
  }
  if (preview.message_count === 0) {
    return 'いま消えるメッセージはありません。これからは、保存期間を過ぎたものから自動で消えます。';
  }
  const photos =
    preview.attachment_count > 0 ? `と${n(preview.attachment_count)}件の写真などのファイル` : '';
  return `いま${n(preview.message_count)}件のメッセージ${photos}が消えます。`;
}

/**
 * 保存期間の確認の窓の見出しと押すボタンの言葉（#475 W-17）。
 * 短くする（無期限に期限を付ける）ときのほか、延ばす・同じ長さにするときも、期限を過ぎて
 * 夜間の片付けを待っているものがその場で消えるなら窓を出すので、そのときは「変えます」と言う。
 */
export function retentionDialogWords(
  currentDays: number | null,
  shorter: boolean,
): { title: string; confirmLabel: string } {
  if (!shorter) return { title: '保存期間を変えます', confirmLabel: '保存期間を変える' };
  return currentDays === null
    ? { title: '保存期間を付けます', confirmLabel: '保存期間を付ける' }
    : { title: '保存期間を短くします', confirmLabel: '保存期間を短くする' };
}

/**
 * 延ばす・同じ長さにするのに消えるものがあるときに添える、なぜ消えるのかの1文（#475 W-17）。
 * 1件のときも読めるよう「どれも」とは言わない（統合レビュー）。
 * 延ばすのに消える、と読むと戸惑うので、もともと消える予定だったものだと伝える。
 */
export const EXPIRED_BACKLOG_NOTE =
  '消えるのは、いまの保存期間をすでに過ぎていて、今夜の処理で消える予定だったものです。';

/** 確認の窓の「元に戻せるか」の1行。 */
export function expiringReversibility(preview: RetentionChangePreview): string {
  return losesMessages(preview)
    ? '元に戻せません。この変更は操作履歴に残ります。'
    : 'この変更は操作履歴に残ります。';
}

/** 押したあとの結果の帯に添える1文（消したものが無ければ空）。 */
export function purgedSentence(outcome: RetentionPurgeOutcome | undefined): string {
  if (!outcome) return '';
  if (outcome.state === 'in_progress') {
    return `期限を過ぎたメッセージを消しています（いままでに${n(outcome.purged_messages)}件）。残りも続けて消えます。`;
  }
  if (outcome.purged_messages === 0) return '';
  const photos =
    outcome.purged_attachments > 0
      ? `と${n(outcome.purged_attachments)}件の写真などのファイル`
      : '';
  return `期限を過ぎた${n(outcome.purged_messages)}件のメッセージ${photos}を消しました。`;
}

/** その場で消し始められなかったときの次の一手（消すのは夜間の処理が行う）。 */
export function purgeNextStep(outcome: RetentionPurgeOutcome | undefined): string | undefined {
  return outcome?.state === 'deferred'
    ? '期限を過ぎたメッセージは、今夜の処理で消えます。'
    : undefined;
}

/** 「事故対応」の指定を外したあとの結果の帯に添える1文。 */
export function afterEvidenceRemoved(
  preview: RetentionChangePreview | null,
  outcome: RetentionPurgeOutcome | undefined,
): string {
  const purged = purgedSentence(outcome);
  if (purged !== '') return purged;
  return preview?.retention_days === null
    ? '保存期間は無期限なので、自動では消えません。'
    : '保存期間を過ぎたものから自動で消えるようになりました。';
}

/**
 * 保存期間・「事故対応」の指定を変えられない方への注記（役職の呼び名はその会社のもの・issue #480）。
 * 「◯◯のは、本社総務の方だけです。変更が必要なときは、ご依頼ください。」
 *
 * 挙げる役職は、操作ごとの実際の判定に合わせる（issue #483）。
 *  - 全社のトーク・1対1 … どの操作も、受け持ちの範囲が「全社」の役職だけ
 *  - 拠点のトーク … 保存日数を変える・指定を付けるのは、拠点の役職でもできる。
 *    **「事故対応」の指定を外す**のと、**無期限のトークに保存期間を付ける**のは全社の役職だけ
 *    （サーバの retention/policy.ts）なので、拠点の役職も変えられる会社では、その操作だけ分けて言う
 *    「保存期間と「事故対応」の指定を変えられるのは、本社総務・支店長・経理の方だけです。
 *     ただし、「事故対応」の指定を外せるのは、本社総務・経理の方だけです。変更が必要なときは、ご依頼ください。」
 */
export function retentionOnlyNote(
  wording: RoleWording,
  channel: {
    /** 全社のトーク・1対1か */
    companyWide: boolean;
    /** いまの保存期間が無期限か（期限を付けるのは全社の役職だけ） */
    unlimited: boolean;
  },
): string {
  const lead = '保存期間と「事故対応」の指定を変えられるのは';
  const main = channel.companyWide ? ROLE_REQUIREMENTS.orgRetention : ROLE_REQUIREMENTS.retention;
  const names = wording.names(main);
  const sentences = [wording.only(main, lead)];
  if (
    !channel.companyWide &&
    names !== null &&
    names !== wording.names(ROLE_REQUIREMENTS.orgRetention)
  ) {
    sentences.push(
      wording.only(
        ROLE_REQUIREMENTS.orgRetention,
        channel.unlimited
          ? 'ただし、「事故対応」の指定を外すことと、無期限のトークに保存期間を付けることができるのは'
          : 'ただし、「事故対応」の指定を外せるのは',
      ),
    );
  }
  if (names !== null) sentences.push('変更が必要なときは、ご依頼ください。');
  return sentences.join('');
}
